import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Check, Sparkles, Tag, X } from 'lucide-react';
import type { CategorySource, MailCategory } from '@mailpilot/shared';
import { Menu } from '../ui/Overlay';

export const SOURCE_LABEL: Record<CategorySource, string> = {
  user: 'Chosen by you',
  learned: 'Learned from your earlier choices',
  ai: 'Sorted by AI',
  heuristic: 'Sorted automatically',
};

/** Small coloured pill for a category. `onDark` for use on the selected (accent) row. */
export function CategoryChip({ category, source, onDark, className }: { category: MailCategory; source?: CategorySource | null; onDark?: boolean; className?: string }) {
  return (
    <span
      title={source ? `${category.name} · ${SOURCE_LABEL[source]}` : category.name}
      className={clsx(
        'inline-flex h-[18px] max-w-[8.5rem] items-center gap-1 truncate rounded-full px-2 text-[0.625rem] font-semibold',
        onDark ? 'bg-white/20 text-white' : 'text-ink',
        className,
      )}
      style={onDark ? undefined : { background: `${category.color}30` }}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: onDark ? '#fff' : category.color }} aria-hidden />
      <span className="truncate">{category.name}</span>
      {source === 'ai' && <Sparkles className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden />}
    </span>
  );
}

/** Dropdown to choose a category (only visible categories are offered). */
export function CategoryPicker({
  categories,
  value,
  onChange,
  trigger,
  allowClear = true,
  clearLabel = 'No category',
  align = 'left',
  label = 'Category',
}: {
  categories: MailCategory[];
  value: string | null;
  onChange: (key: string | null) => void;
  trigger: (p: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu'; id: string }) => ReactNode;
  allowClear?: boolean;
  clearLabel?: string;
  align?: 'left' | 'right';
  label?: string;
}) {
  const visible = categories.filter((c) => !c.hidden);
  return (
    <Menu
      label={label}
      align={align}
      width={230}
      trigger={trigger}
      items={[
        ...visible.map((c) => ({
          key: c.key,
          label: c.name,
          icon: <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.color }} />,
          active: value === c.key,
          trailing: value === c.key ? <Check className="h-4 w-4" /> : c.total ? <span className="text-[0.6875rem] text-muted">{c.total}</span> : undefined,
          onSelect: () => onChange(c.key),
        })),
        ...(allowClear ? ['divider' as const, { key: '__none', label: clearLabel, icon: <X className="h-4 w-4" />, active: value === null, onSelect: () => onChange(null) }] : []),
      ]}
    />
  );
}

export const CategoryIcon = Tag;
